import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { ShoppingCart, DollarSign, Package } from 'lucide-react';

import CampaignRangeSelector from '../../../components/CampaignRangeSelector';
import BasketItemsChart from './BasketItemsChart';
import PerformanceCardSkeleton from '../../../components/PerformanceCardSkeleton';
import { formatCurrency2SigFig, formatWholeNumberNaN } from '../../../../../../../core/utils/campaigns';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';
import KpiBar, { KpiCard } from '../../../../../../shared/designSystem/components/KpiBar';

// =============================================================================
// BasketItemsPerformance — unified Performance card for the V2 Basket Items
// tab. One section: head (eyebrow + range selector), 3-column KPI bar
// (Transactions / Sub Total / Quantity), trend chart. Mirrors the Summary /
// Display / Transactions / SignUps Performance pattern.
//
// Sub Total carries the "Revenue before taxes, shipping and promotions"
// tooltip that v1 put next to the KPI title via an ExtraInfo badge.
// =============================================================================

const BasketItemsPerformance = ({
  detailBarSummary = { transactions: 0, revenue: 0, quantity: 0 },
  byDate,
  dateRange,
  onDateChange,
  campaignOrder,
  loading = false,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  const [rangeType, setRangeType] = useState('Custom');

  // Default range for the Custom pill fallback label — data's own min/max
  // if available, else campaign flight dates.
  const defaultDateRange = useMemo(() => {
    if (byDate && byDate.length > 0) {
      const dates = byDate.map((d) => d.date).filter(Boolean).sort();
      if (dates.length > 0) {
        return [moment(dates[0]), moment(dates[dates.length - 1])];
      }
    }
    if (campaignOrder && campaignOrder.startDate && campaignOrder.endDate) {
      return [moment(campaignOrder.startDate), moment(campaignOrder.endDate)];
    }
    return null;
  }, [byDate, campaignOrder]);

  if (loading) return <PerformanceCardSkeleton />;

  return (
    <section className="campaign-overview__performance">
      <header className="campaign-overview__performance-head">
        <div className="campaign-overview__performance-eyebrow mono-eyebrow">
          Performance
        </div>
        <CampaignRangeSelector
          rangeType={rangeType}
          byCampaignRangeDate={dateRange}
          setRangeType={setRangeType}
          setByCampaignRangeDate={(newRange) => {
            if (onDateChange) onDateChange(newRange);
          }}
          timezone={effectiveTimezone}
          defaultDateRange={defaultDateRange}
        />
      </header>

      <KpiBar>
        <KpiCard
          icon={<ShoppingCart size={16} />}
          iconTint="primary"
          label="Transactions"
          value={formatWholeNumberNaN(detailBarSummary.transactions)}
        />
        <KpiCard
          icon={<DollarSign size={16} />}
          iconTint="emerald"
          label="Sub Total"
          labelTooltip="Revenue before taxes, shipping and promotions"
          value={formatCurrency2SigFig(detailBarSummary.revenue)}
        />
        <KpiCard
          icon={<Package size={16} />}
          iconTint="indigo"
          label="Quantity"
          labelTooltip="Total number of product items within all selected transactions"
          value={formatWholeNumberNaN(detailBarSummary.quantity)}
        />
      </KpiBar>

      <BasketItemsChart byDate={byDate} />
    </section>
  );
};

export default BasketItemsPerformance;
